import { outreachApiConfig } from "../config/api.config";

/**
 * Payload for the add-contact endpoint.
 */
export interface ResubscribePayload {
  email: string;
  topic?: string;
  source?: string;
}

/**
 * Response format returned by the add-contact endpoint.
 */
export interface ResubscribeResponse {
  success: boolean;
  message?: string;
}

const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, "");

const buildEndpoint = (): string => {
  const base = trimTrailingSlash(outreachApiConfig.baseUrl);
  return `${base}${outreachApiConfig.addContactPath}`;
};

const ADD_CONTACT_ENDPOINT = buildEndpoint();
let axiosModulePromise: Promise<typeof import("axios")> | null = null;

const loadAxiosModule = (): Promise<typeof import("axios")> => {
  if (!axiosModulePromise) {
    axiosModulePromise = import("axios");
  }
  return axiosModulePromise;
};

export const resubscribeContact = async (
  email: string,
  topic?: string
): Promise<ResubscribeResponse> => {
  const axiosModule = await loadAxiosModule();
  const axiosInstance = axiosModule.default;
  const { isAxiosError } = axiosModule;

  try {
    const payload: ResubscribePayload = {
      email,
      topic,
      source: "unsubscribe-confirmation",
    };

    const response = await axiosInstance.post<ResubscribeResponse>(
      ADD_CONTACT_ENDPOINT,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.data.success) {
      throw new Error(
        response.data.message || "Failed to re-subscribe at this time."
      );
    }

    return {
      success: true,
      message: response.data.message || "You have been re-subscribed.",
    };
  } catch (error) {
    if (isAxiosError(error) && "response" in error && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Unable to reach the subscription service.");
  }
};
